import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { FollowUpRow } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, todayIso } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Badge, Button, Checkbox } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ReferralsCard } from './VisitOrdersPanel';

type View = 'pending' | 'booked' | 'done';

function weekAhead(): string {
  return new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
}

export function FollowUpsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('pending');
  const [search, setSearch] = useState('');
  const [dueSoonOnly, setDueSoonOnly] = useState(false);

  const followUps = useInfiniteQuery({
    queryKey: ['follow-ups', view, search, dueSoonOnly],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<{ data: FollowUpRow[]; nextCursor: string | null }>(
        `/follow-ups${qs({
          status: view,
          q: search || undefined,
          to: view === 'pending' && dueSoonOnly ? weekAhead() : undefined,
          limit: 50,
          cursor: pageParam,
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = followUps.data?.pages.flatMap((page) => page.data);

  const close = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'done' | 'cancelled' }) => put(`/follow-ups/${id}`, { status }),
    onSuccess: () => {
      toast.success(tr('opd', 'follow_up_updated'));
      void queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_update_the_follow_up'), describeError(error)),
  });

  const book = (row: FollowUpRow) => {
    const query = qs({ book: 1, type: 'follow_up', patientId: row.patient.id, doctorId: row.doctor?.id });
    navigate(`/appointments${query}`);
  };

  return (
    <>
      <PageHeader
        title={tr('opd', 'follow_ups_due')}
        description={tr('opd', 'patients_a_doctor_asked_to_see_again_they_get_a')}
      />

      <ReferralsCard scope="all" />

      <Tabs
        active={view}
        onChange={(next) => setView(next as View)}
        tabs={[
          { id: 'pending', label: tr('opd', 'to_book') },
          { id: 'booked', label: tr('opd', 'booked') },
          { id: 'done', label: tr('opd', 'seen') },
        ]}
      />

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('opd', 'search_name_mrn_or_phone') }}
        filters={
          view === 'pending' && (
            <FilterField><Checkbox checked={dueSoonOnly} onCheckedChange={setDueSoonOnly} label={tr('opd', 'due_within_a_week_and_overdue')} /></FilterField>
          )
        }
        onReset={
          search || (view === 'pending' && dueSoonOnly)
            ? () => {
                setSearch('');
                setDueSoonOnly(false);
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={followUps.isLoading} refreshing={followUps.isFetching}
        error={followUps.error}
        rowKey={(row) => row.id}
        empty={view === 'pending' ? tr('opd', 'nobody_is_waiting_to_be_booked_for_a_follow_up') : tr('opd', 'nothing_here_yet')}
        footer={
          followUps.hasNextPage ? (
            <Button variant="outline" className="mx-auto" onClick={() => void followUps.fetchNextPage()} loading={followUps.isFetchingNextPage}>
              {tr('opd', 'show_more')}
            </Button>
          ) : undefined
        }
        columns={[
          {
            header: tr('opd', 'due'),
            width: '150px',
            sort: (row) => row.dueDate,
            cell: (row) => (
              <div>
                <p className="tabular">{dateOnly(row.dueDate)}</p>
                {row.overdue && <Badge variant="destructive">{tr('opd', 'overdue')}</Badge>}
                {!row.overdue && row.status === 'pending' && row.dueDate === todayIso() && <Badge variant="warning">{tr('opd', 'today')}</Badge>}
              </div>
            ),
          },
          {
            header: tr('opd', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient.mrn}
                  {row.patient.phone ? ` · ${row.patient.phone}` : ' · no phone on file'}
                </p>
              </div>
            ),
          },
          { header: tr('opd', 'doctor'), sort: (row) => row.doctor?.name, cell: (row) => row.doctor?.name ?? '—' },
          {
            header: tr('opd', 'reminder'),
            sort: (row) => row.reminderSentAt,
            cell: (row) =>
              row.reminderSentAt ? <Badge variant="success">{tr('opd', 'sent')}</Badge> : <span className="text-xs text-muted-foreground">{tr('opd', 'not_yet')}</span>,
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              can('appointment.manage') && (
                <div className="flex justify-end gap-1">
                  {row.status === 'pending' && (
                    <Button size="sm" onClick={() => book(row)}>
                      {tr('opd', 'book')}
                    </Button>
                  )}
                  {['pending', 'booked'].includes(row.status) && (
                    <Button size="sm" variant="ghost" onClick={() => close.mutate({ id: row.id, status: 'cancelled' })}>
                      {tr('opd', 'not_needed')}
                    </Button>
                  )}
                </div>
              ),
          },
        ]}
      />
    </>
  );
}
